import axios from 'axios';
import { CONSTANTS } from '@factories/constants';
import { ResetPasswordRequest } from '@customTypes/PropTypes';

axios.defaults.headers['Content-Type'] = 'application/json';

/** Get Token from params */
export function appendDeepLinkingQueryParams(url: string): string {
    let deepLinkingParams = '';
    const urlParams = new URLSearchParams(window.location.search.slice(1));

    if (urlParams.has(CONSTANTS.APP_ID) && urlParams.has(CONSTANTS.PAYLOAD)) {
        deepLinkingParams = `app_id=${urlParams.get(CONSTANTS.APP_ID)}&payload=${urlParams.get(CONSTANTS.PAYLOAD)}`;
    }

    if (urlParams.has(CONSTANTS.REDIRECT_URL)) {
        deepLinkingParams = `${CONSTANTS.REDIRECT_URL}=${urlParams.get(CONSTANTS.REDIRECT_URL)}`;
    }

    const deepLinkingQueryUrl = deepLinkingParams ? `${url}?${deepLinkingParams}` : url;

    return deepLinkingQueryUrl;
}

export async function validateToken(token: string | null): Promise<any> {
    const data = { token };
    return axios
        .post('/services/auth/validateToken', data)
        .then((result) => {
            if (!result.data.status) return Promise.reject(result.data.status);
            return result.data;
        })
        .catch((err) => {
            if (err.code === 400) {
                return Promise.reject(
                    new Error('ResetPassword Token Validation:Failed')
                );
            }
            return Promise.reject(new Error('ResetPassword Token Validation:Failed'));
        });
}

export async function signup(values: any): Promise<any> {
    try {
        const urlParams = new URLSearchParams(window.location.search.slice(1));

        const response = await axios.post('/services/auth/newUser', {
            ...values,
            redirectUrl: urlParams.get('redirect_url') || '',
            encryptedDuns: urlParams.get('dunsNumber') || '',
            plaVersion: values.acceptTerms ? '1' : '0',
            countryId: urlParams.get('countryId') || values.country || 'US',
        });
        return response.data;
    }
    catch (error: any) {
        if (axios.isAxiosError(error)) {
            const errorResponse = error?.response?.data;
            throw errorResponse; // returns the Json response from cpl-services
        }
        else {
            throw new Error('An unexpected error occurred');
        }
    }
}

export async function resetPassword(data: ResetPasswordRequest, passwordExpired: boolean, continueRegistration: boolean): Promise<any> {
    let url = '/services/auth/resetPassword';
    let callMethod = 'PUT';
    if (passwordExpired) {
        url = '/services/auth/verifyExpiredPswd';
    }
    else if (continueRegistration) {
        url = '/services/auth/continueRegistration';
        callMethod = 'POST';
    }

    const axiosCall = axios({
        method: callMethod as any,
        url,
        data,
    });
    return axiosCall
        .then((result) => {
            if (!result.data.status) return Promise.reject(result.data.status);
            return result.data;
        })
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}

export function forgotPassword(email: string, flow: string): Promise<any> {
    // Constructing Redirect_URL request query param
    const urlParams = new URLSearchParams(window.location.search.slice(1));
    let urlConstruct = '';
    // append DUNs number to request URL
    const urlDunsNum = urlParams.get('dunsNumber');
    const encryptedDunsParam = urlDunsNum ? `encryptedDuns=${urlDunsNum}` : '';
    if (encryptedDunsParam) {
        urlConstruct += `&${encryptedDunsParam}`;
    }
    // append Type of flow to request URL
    let flowType = '';
    if (flow) {
        flowType = (flow === 'DUNS') ? (`flow=${encryptedDunsParam === '' ? 'DUNS' : 'ExistDUNS'}`) : `flow=${flow}`;
    }
    if (flowType) {
        urlConstruct += `&${flowType}`;
    }
    // append Country ID to request URL
    const countryId = urlParams.get('countryId');
    const countryIdParam = countryId ? `countryId=${countryId}` : '';
    if (countryIdParam) {
        urlConstruct += `&${countryIdParam}`;
    }
    // append redirect URL to request URL
    const redirectUrl = urlParams.get('redirect_url');
    const redirectUrlParam = redirectUrl ? `redirect_url=${redirectUrl}` : '';
    if (redirectUrlParam) {
        urlConstruct += `&${redirectUrlParam}`;
    }
    // append app_id & Payload  to request URL
    let appId_payLoad = '';
    if (urlParams.has(CONSTANTS.APP_ID) && urlParams.has(CONSTANTS.PAYLOAD)) {
        appId_payLoad = `app_id=${urlParams.get(CONSTANTS.APP_ID)}&payload=${urlParams.get(CONSTANTS.PAYLOAD)}`;
    }
    if (appId_payLoad) {
        urlConstruct += `&${appId_payLoad}`;
    }

    urlConstruct = urlConstruct ? `?${urlConstruct}`.replace('?&', '?') : '';
    const deepLinkingQueryUrl = `/services/auth/forgotPassword${urlConstruct}`;
    return axios.post(deepLinkingQueryUrl, { email });
}

export async function oktaLogin(idToken: string, passwordExpiry: boolean): Promise<any> {
    const deepLinkingQueryUrl = appendDeepLinkingQueryParams(
        '/services/auth/oktaLogin'
    );

    const data = { idToken, passwordExpiredFlag: passwordExpiry, cache: true };

    try {
        return await axios
            .post(deepLinkingQueryUrl, data)
            .then((result: any) => {
                if (!result.data.status) {
                    return Promise.reject(result.data.status);
                }
                if (result.data.status.code === 200 && result.data.owl) {
                    window.localStorage.setItem('SHOW_OWL_RESPONSE_BANNER', (result.data.owl.error) ? 'true' : 'false');
                    return { ...result.data, owlResponse: result.data.owl };
                }
                window.localStorage.setItem('SHOW_OWL_RESPONSE_BANNER', 'false');
                return Promise.resolve(result.data);
            })
            .catch((error) => Promise.reject(error.data.status));
    }
    catch (err: any) {
        if (err.code === 400) {
            return Promise.reject(new Error('Okta Session creation in BE:Failed'));
        }
        return Promise.reject(new Error('Okta Session creation in BE:Failed'));
    }
}

export async function oktaLogout(): Promise<any> {
    try {
        await axios.get('/services/auth/oktaLogout').then((response) => response);
    }
    catch (error) {
        return Promise.reject(
            new Error('Issue with the Okta Logout api call from the BE')
        );
    }
}

export async function confirmUserRegistration(token: string | null, product: string | null): Promise<any> {
    const axiosCall = axios({
        method: 'POST',
        url: '/services/auth/confirmRegistration',
        data: { token, product },
    });
    return axiosCall
        .then((result) => {
            if (!result.data.status) return Promise.reject(result.data.status);
            return result.data;
        })
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}

export async function socialNetworksRegistration(registrationForm: any): Promise<any> {
    const axiosCall = axios({
        method: 'POST',
        url: '/services/okta/socialNetworksSignup',
        data: { registrationForm },
    });
    return axiosCall
        .then((result) => result.data)
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}
// entitle user to existing duns
export async function entitleExistingDuns(encrypted_duns: string, app_id: number) {
    const axiosCall = axios({
        method: 'POST',
        url: '/services/GAD/entitleExistingDuns',
        data: { encrypted_duns, app_id, sales_channel: CONSTANTS.SALES_CHANNEL },
    });
    return axiosCall
        .then((result) => result.data)
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}

export async function entitleFreemium(app_id: number, encrypted_duns: string, country_id: string): Promise<any> {
    const axiosCall = axios({
        method: 'POST',
        url: '/services/entitlements/entitleFreemium',
        data: {
            app_id, encrypted_duns, country_id, sales_channel: CONSTANTS.SALES_CHANNEL
        },
    });
    return axiosCall
        .then((result) => result.data)
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}

export async function decodeBase64(encrypted_duns: string): Promise<any> {
    const axiosCall = axios({
        method: 'GET',
        url: `/services/entitlements/decode?payload=${encrypted_duns}`,
    });
    return axiosCall
        .then((result) => result.data)
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}

export async function entitleProduct(encrypted_duns: string, app_id: number): Promise<any> {
    const axiosCall = axios({
        method: 'POST',
        url: '/services/GAD/entitleProduct',
        data: { encrypted_duns, app_id, sales_channel: CONSTANTS.SALES_CHANNEL },
    });
    return axiosCall
        .then((result) => result.data)
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}

export async function bloomreachConsent(email: string, os: string, bloomreachConsent: boolean): Promise<any> {
    const axiosCall = axios({
        method: 'POST',
        url: '/services/auth/user/consent',
        data: { email, os, bloomreachConsent },
    });
    return axiosCall
        .then((result) => result.data)
        .catch((err) => {
            const resultData = (err?.response?.data?.error) ? err.response.data : null;
            if (resultData) {
                return Promise.reject(resultData);
            }
            return Promise.reject(new Error(''));
        });
}
